/**
 * Keeps the global filter bar across page loads.
 *
 * One localStorage key holding versioned JSON. Only the filter BAR is saved — never a
 * page's own drill state — and it is saved as a convenience, so every read and write
 * is wrapped the same way ui/preferences.tsx wraps its own: a private window or a
 * browser set to block storage throws on access, and the app has to come up on the
 * defaults regardless.
 *
 * A stored choice can outlive the data it was made from: an employee leaves the
 * bundle, a department is renamed, a month drops off the front. `pruneFilters` puts
 * just that control back to "all" and keeps the rest. Restoring a supervisor nobody
 * has any more would quietly empty every page, which is worse than forgetting the
 * choice — and the scope pills show whatever survived, so nothing is restored unseen.
 */
import { ALL, EMPTY_FILTERS, MULTI_KEYS, TENURE_BANDS, PAYROLL_BANDS } from './filters'
import type { EmploymentStatus, Filters, MultiKey } from './filters'
import type { Derived } from './selectors'
import { valueName } from './blankValue'

/**
 * The Facility group filter's choices: the crosstab's groups by name, every blank
 * spelling folded into one "Unknown" as the Overview's bars draw them (review #177).
 */
export const facilityGroupOptions = (groups: readonly string[]): string[] =>
  [...new Set(groups.map(valueName))].sort()

export const FILTERS_KEY = 'cpc.filters.v1'

/**
 * Bump when the shape of `Filters` changes in a way an old value cannot survive.
 *
 * 2: Employee status defaults to both (2026-09-25). A v1 value is still read, but its
 * 'active' is dropped — that was the default then, and it was saved alongside any
 * other choice, so it cannot be told apart from somebody picking it.
 */
const VERSION = 2

interface Stored {
  v: number
  filters: Filters
}

/**
 * The option lists a stored value has to still belong to. A plain record rather than
 * `Derived` itself, so the pruning can be tested without building a whole rollup.
 */
export interface FilterOptions {
  claimTypes: string[]
  serviceTypes: string[]
  claimStatuses: string[]
  /** Facility groups by name, blank spellings as one "Unknown" (`facilityGroupOptions`). */
  facilityGroups: string[]
  departments: string[]
  jobTitles: string[]
  shores: string[]
  vendors: string[]
  officeLocations: string[]
  supervisors: string[]
  employees: string[]
  months: string[]
}

export const optionsOf = (d: Derived): FilterOptions => ({
  claimTypes: d.claimTypes,
  serviceTypes: d.serviceTypes,
  claimStatuses: d.claimStatuses,
  facilityGroups: facilityGroupOptions(d.facilityGroups),
  departments: d.departments,
  jobTitles: d.jobTitles,
  shores: d.shores,
  vendors: d.vendors,
  officeLocations: d.officeLocations,
  supervisors: d.supervisors,
  employees: d.people.map((p) => p.employee),
  // Every month, not the selected ones: `d.months` is already narrowed by the range
  // being pruned, and checking a range against itself would clear nothing ever.
  months: d.allMonths,
})

const OPTION_LIST: Record<MultiKey, keyof FilterOptions> = {
  claimType: 'claimTypes',
  serviceType: 'serviceTypes',
  claimStatus: 'claimStatuses',
  facilityGroup: 'facilityGroups',
  department: 'departments',
  jobTitle: 'jobTitles',
  shore: 'shores',
  vendor: 'vendors',
  officeLocation: 'officeLocations',
}

const isStringArray = (x: unknown): x is string[] =>
  Array.isArray(x) && x.every((s) => typeof s === 'string')

/** A band index is valid when it names a band that exists; anything else is ALL. */
const bandIndex = (x: unknown, bands: unknown[]): string =>
  typeof x === 'string' && /^\d+$/.test(x) && Number(x) < bands.length ? x : ALL

/**
 * Turns a stored string back into filters. Anything the wrong shape — a missing key,
 * a number where a string should be, an enum value this build does not know — falls
 * back for that key alone. A wrong version or unparseable JSON falls back entirely.
 *
 * Only the DATASET-INDEPENDENT checks live here; whether a department still exists
 * is `pruneFilters`' job, because the option lists are not known until the bundle is.
 */
export function parseStoredFilters(raw: string | null): Filters {
  if (raw === null) return EMPTY_FILTERS
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return EMPTY_FILTERS
  }
  if (!data || typeof data !== 'object') return EMPTY_FILTERS
  const { v, filters } = data as Partial<Stored>
  if ((v !== VERSION && v !== 1) || !filters || typeof filters !== 'object') return EMPTY_FILTERS

  const s = filters as unknown as Record<string, unknown>
  const str = (k: keyof Filters) => (typeof s[k] === 'string' ? (s[k] as string) : ALL)
  const oneOf = <T extends string>(k: keyof Filters, allowed: readonly T[], fallback: T): T =>
    (allowed as readonly string[]).includes(s[k] as string) ? (s[k] as T) : fallback

  const out: Filters = { ...EMPTY_FILTERS }
  for (const k of MULTI_KEYS) if (isStringArray(s[k])) out[k] = s[k] as string[]
  out.supervisor = str('supervisor')
  out.employee = str('employee')
  out.management = oneOf('management', ['all', 'yes', 'no'] as const, 'all')
  out.employmentStatus = oneOf<EmploymentStatus>(
    'employmentStatus',
    v === 1 ? ['terminated'] : ['active', 'both', 'terminated'],
    EMPTY_FILTERS.employmentStatus,
  )
  out.tenure = bandIndex(s.tenure, TENURE_BANDS)
  out.payrollBand = bandIndex(s.payrollBand, PAYROLL_BANDS)
  out.monthFrom = str('monthFrom')
  out.monthTo = str('monthTo')
  return out
}

/**
 * Drops any value the loaded dataset no longer offers. A multi-select keeps whatever
 * of its picks still exist; a single-select that names something gone goes back to
 * ALL. Returns the SAME object when nothing changed, so a caller can set state with
 * it without forcing a re-render.
 */
export function pruneFilters(f: Filters, o: FilterOptions): Filters {
  let changed = false
  const out: Filters = { ...f }

  for (const k of MULTI_KEYS) {
    const list = o[OPTION_LIST[k]]
    const kept = f[k].filter((v) => list.includes(v))
    if (kept.length !== f[k].length) {
      out[k] = kept
      changed = true
    }
  }

  const single = (k: 'supervisor' | 'employee' | 'monthFrom' | 'monthTo', list: string[]) => {
    if (f[k] !== ALL && !list.includes(f[k])) {
      out[k] = ALL
      changed = true
    }
  }
  single('supervisor', o.supervisors)
  single('employee', o.employees)
  single('monthFrom', o.months)
  single('monthTo', o.months)

  return changed ? out : f
}

export function loadStoredFilters(): Filters {
  try {
    return parseStoredFilters(localStorage.getItem(FILTERS_KEY))
  } catch {
    return EMPTY_FILTERS
  }
}

export function saveStoredFilters(f: Filters): void {
  try {
    const stored: Stored = { v: VERSION, filters: { ...f, slice: null } }
    localStorage.setItem(FILTERS_KEY, JSON.stringify(stored))
  } catch {
    /* a remembered filter is a convenience; failing to keep it is not an error */
  }
}

export function clearStoredFilters(): void {
  try {
    localStorage.removeItem(FILTERS_KEY)
  } catch {
    /* as above */
  }
}
